import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useAddPurchaseOrdersMutation,
  useUpdatePurchaseOrdersMutation,
} from "../../../../api/services/client/clientApis";
import {
  AddPurchaseOrderParams,
  ErrorModel,
  GetPurchaseOrderResponse,
  UpdatePurchaseOrderParams,
} from "../../../../api/services/client/models";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiModal,
  MuiSelect,
  MuiTextField,
  MuiTypography,
  ToastDisplayType,
} from "../../../../components";
import { MuiDateFieldFormController } from "../../../../components/dateField/MuiDateFieldFormController";
import { STATUS_BADGE_TYPE } from "../../../../components/statusBadge/StatusBadge";
import { CURRENCY_FORMAT_MAP } from "../../../../components/textFieldCurrency/CurrencyUtils";
import { TextFieldCurrency } from "../../../../components/textFieldCurrency/TextFieldCurrency";
import {
  BE_ERROR_CODES,
  MAX_LENGTH_1000,
  MAX_LENGTH_30,
  ONE_DAY_IN_MS,
  PAGE_IDS,
  REGEX_ACCEPTED_PO_NUMBER_SPECIAL_CHARS_AND_SPACES,
  TOAST_AUTO_HIDE_DURATION,
} from "../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../helpers/constants/componentIds";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import {
  convertToUserFormat,
  convertToUTCFormat,
  getFormattedAmount,
  getNameByIdFromList,
  getParsedBEErrors,
  hasKey,
  isNotEmpty,
  replaceHashWithValue,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { currencyListSelector } from "../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { AddPurchaseOrderFormKey } from "../../modelsAndSchemas/Models";
import { AddPurchaseOrderValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { purchaseOrdersListSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ConfirmatoryPopupWrapper } from "../addNewContact/AddNewContact.style";
import { AddPurchaseOrderWrapper } from "./AddPurchaseOrder.style";

interface AddPurchaseOrderProps {
  open: boolean;
  onClose: () => void;
  onPurchaseOrderAdded?: (purchaseOrderId?: string) => void;
  isEdit?: boolean;
  purchaseOrderData?: GetPurchaseOrderResponse;
}

export const AddPurchaseOrder: FC<AddPurchaseOrderProps> = ({
  open,
  onClose,
  onPurchaseOrderAdded,
  isEdit = false,
  purchaseOrderData,
}) => {
  const pageId = PAGE_IDS.ADD_PURCHASE_ORDER;
  const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };
  const { isMobile } = useMediaQueries();
  const dispatch = useDispatch();
  const { id: clientId } = useSelector(createClientSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValidationMessage,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const [showConfirmatoryPopup, setShowConfirmatoryPopup] = useState(false);
  const [currencyLocale, setCurrencyLocale] = useState(
    CURRENCY_FORMAT_MAP.USD.locale,
  );
  const confirmatoryMessageRef = useRef<string>("");
  const buttonClickedRef = useRef<string>("");

  const {
    purchaseOrderTitle,
    addPurchaseOrderHeading,
    editPurchaseOrderHeading,
    poNumber,
    poEffectiveDate,
    poExpirationDate,
    poStatus,
    poAmount,
    statusList,
    cancelActionConfirmLabel,
    addActionConfirmLabel,
    editCancelActionConfirmLabel,
    updateActionConfirmLabel,
    poAddSuccessMessage,
    poUpdateSuccessMessage,
    expirationDateMustBeGreaterThanEffectiveDate,
    effectiveDateMustBeLesserThanExpirationDate,
    poNumberUniqueErrorMessage,
  } = purchaseOrdersListSiteCoreKeys;

  const getLabel = useCallback(
    (key: string) => {
      return fetchLabel(purchaseOrderTitle, key);
    },
    [fetchLabel, purchaseOrderTitle],
  );

  const buttonLabels = useMemo(
    () => ({
      cancelMessage: getLabel(cancelActionConfirmLabel),
      saveMessage: getLabel(addActionConfirmLabel),
      editCancelMessage: getLabel(editCancelActionConfirmLabel),
      updateMessage: getLabel(updateActionConfirmLabel),
      buttonSave: commonLabels.save,
      buttonCancel: commonLabels.cancel,
      buttonUpdate: commonLabels.update,
    }),
    [
      getLabel,
      cancelActionConfirmLabel,
      addActionConfirmLabel,
      editCancelActionConfirmLabel,
      updateActionConfirmLabel,
      commonLabels,
    ],
  );

  const {
    cancelMessage,
    saveMessage,
    editCancelMessage,
    updateMessage,
    buttonSave,
    buttonCancel,
    buttonUpdate,
  } = buttonLabels;

  const methods = useForm({
    mode: "onBlur",
    defaultValues: {
      [AddPurchaseOrderFormKey.poNumber]: "",
      [AddPurchaseOrderFormKey.effectiveDate]: "",
      [AddPurchaseOrderFormKey.currencyTypeId]: "",
      [AddPurchaseOrderFormKey.initialAmount]: "",
      [AddPurchaseOrderFormKey.initialAmountFormatted]: "",
      [AddPurchaseOrderFormKey.statusId]: STATUS_BADGE_TYPE.active,
      [AddPurchaseOrderFormKey.expiryDate]: "",
      [AddPurchaseOrderFormKey.notes]: "",
    },
    resolver: yupResolver(
      AddPurchaseOrderValidationSchema(
        {
          ...commonValidationMessages,
          expirationDateMustBeGreaterThanEffectiveDate: getLabel(
            expirationDateMustBeGreaterThanEffectiveDate,
          ),
          effectiveDateMustBeLesserThanExpirationDate: getLabel(
            effectiveDateMustBeLesserThanExpirationDate,
          ),
        },
        userCountryDateFormat,
      ),
    ),
  });

  const {
    formState: { dirtyFields },
    control,
    getValues,
    handleSubmit,
    setValue,
    reset,
    setError,
  } = methods;

  const {
    mutateAsync: addPurchaseOrder,
    isPending: isAddPurchaseOrderPending,
    isSuccess: isAddPurchaseOrderSuccess,
    isError: isAddPurchaseOrderError,
    error: addPurchaseOrderError,
  } = useAddPurchaseOrdersMutation();

  const {
    mutateAsync: updatePurchaseOrder,
    isSuccess: isUpdatePurchaseOrderSuccess,
    isPending: isUpdatePurchaseOrderPending,
    isError: isUpdatePurchaseOrderError,
    error: updatePurchaseOrderApiError,
  } = useUpdatePurchaseOrdersMutation();

  const currencyList = useSelector(currencyListSelector);

  useEffect(() => {
    if (!isEdit && currencyList) {
      setValue(AddPurchaseOrderFormKey.currencyTypeId, currencyList?.[0]?.id);
      handleCurrencyChange();
    }
  }, [currencyList]);

  useEffect(() => {
    if (isEdit && purchaseOrderData) {
      const currencyName = getNameByIdFromList(
        currencyList,
        purchaseOrderData?.currencyTypeId,
      );

      const currencyFormat = CURRENCY_FORMAT_MAP[currencyName];
      setCurrencyLocale(currencyFormat?.locale);

      reset({
        [AddPurchaseOrderFormKey.poNumber]: purchaseOrderData.poNumber,
        [AddPurchaseOrderFormKey.effectiveDate]: convertToUserFormat(
          purchaseOrderData.effectiveDate,
          userCountryDateFormat,
        ),
        [AddPurchaseOrderFormKey.effectiveDateWithoutFormatted]: dayjs(
          purchaseOrderData.effectiveDate,
        ),
        [AddPurchaseOrderFormKey.currencyTypeId]:
          purchaseOrderData?.currencyTypeId,
        [AddPurchaseOrderFormKey.initialAmount]:
          purchaseOrderData?.currentAmount,
        [AddPurchaseOrderFormKey.initialAmountFormatted]: `${getFormattedAmount(
          Number(purchaseOrderData?.currentAmount),
          currencyLocale,
        )}`,
        [AddPurchaseOrderFormKey.statusId]: purchaseOrderData.statusId,
        [AddPurchaseOrderFormKey.expiryDate]: purchaseOrderData?.expiryDate
          ? convertToUserFormat(
              purchaseOrderData.expiryDate,
              userCountryDateFormat,
            )
          : "",
        [AddPurchaseOrderFormKey.expiryDateWithoutFormatted]:
          purchaseOrderData?.expiryDate
            ? dayjs(purchaseOrderData.expiryDate).utc(false).startOf("day") // ignore extra 23:59:59 hour min sec
            : null,
        [AddPurchaseOrderFormKey.notes]: purchaseOrderData?.notes
          ? purchaseOrderData.notes
          : "",
      });
    }
  }, [isEdit]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isAddPurchaseOrderPending || isUpdatePurchaseOrderPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
  }, [isAddPurchaseOrderPending, isUpdatePurchaseOrderPending]);

  const dispatchToast = (message: string, type?: ToastDisplayType) => {
    dispatch(
      toggleToast({
        open: true,
        message,
        type,
        autoHideDuration: TOAST_AUTO_HIDE_DURATION,
      }),
    );
  };

  const setCustomErrorBeneathTheField = () => {
    setError(AddPurchaseOrderFormKey.poNumber, {
      type: "custom",
      message: getLabel(poNumberUniqueErrorMessage),
    });
  };

  const showErrorsFromBE = (error: ErrorModel) => {
    if (error.errorCode === BE_ERROR_CODES.PO_NUMBER_UNIQUE) {
      setCustomErrorBeneathTheField();
    }
  };

  useEffect(() => {
    if (isAddPurchaseOrderError || isUpdatePurchaseOrderError) {
      dispatch(toggleLoader({ isVisible: false }));
      const errorObj = isAddPurchaseOrderError
        ? addPurchaseOrderError
        : updatePurchaseOrderApiError;
      const error = getParsedBEErrors(errorObj)[0];
      if (isNotEmpty(error) && hasKey(error, "errorCode")) {
        showErrorsFromBE(error);
      } else {
        const errorMessage =
          error && hasKey(error, "message")
            ? error?.message
            : commonLabels.someThingWentWrongPleaseTryAgainLater;
        dispatchToast(errorMessage, "error");
      }
    }
  }, [
    isAddPurchaseOrderError,
    addPurchaseOrderError,
    isUpdatePurchaseOrderError,
    updatePurchaseOrderApiError,
  ]);

  useEffect(() => {
    if (isAddPurchaseOrderSuccess || isUpdatePurchaseOrderSuccess) {
      onClose();
      dispatch(
        toggleToast({
          open: true,
          message: getLabel(
            isAddPurchaseOrderSuccess
              ? poAddSuccessMessage
              : poUpdateSuccessMessage,
          ),
          type: "success",
          autoHideDuration: TOAST_AUTO_HIDE_DURATION,
        }),
      );

      onPurchaseOrderAdded?.(
        isUpdatePurchaseOrderSuccess
          ? purchaseOrderData?.purchaseOrderId
          : undefined,
      );
    }
  }, [isAddPurchaseOrderSuccess, isUpdatePurchaseOrderSuccess]);

  const handleConfirmatoryPopup = (buttonTitle: string) => {
    buttonClickedRef.current = buttonTitle;
    if (buttonTitle === buttonSave) {
      confirmatoryMessageRef.current = saveMessage;
    } else if (buttonTitle === buttonCancel) {
      confirmatoryMessageRef.current = isEdit
        ? editCancelMessage
        : cancelMessage;
    } else if (buttonTitle === buttonUpdate) {
      confirmatoryMessageRef.current = updateMessage;
    }
    if (!showConfirmatoryPopup) {
      setShowConfirmatoryPopup(true);
    }
  };

  const checkFieldValidations = () => {
    handleConfirmatoryPopup(
      isEdit && purchaseOrderData ? buttonUpdate : buttonSave,
    );
  };

  const handleOnClose = () => {
    if (isNotEmpty(dirtyFields)) {
      handleConfirmatoryPopup(buttonCancel);
    } else {
      onClose();
    }
  };

  const clearRefs = () => {
    buttonClickedRef.current = "";
    confirmatoryMessageRef.current = "";
  };

  const hideConfirmatoryPopup = () => {
    if (showConfirmatoryPopup) {
      setShowConfirmatoryPopup(false);
      clearRefs();
    }
  };

  const getValuesFromForm = () => {
    const {
      effectiveDate,
      initialAmount,
      poNumber,
      currencyTypeId,
      expiryDate,
      notes,
      statusId,
    } = getValues();

    const params: AddPurchaseOrderParams = {
      clientId,
      poNumber,
      effectiveDate: convertToUTCFormat(effectiveDate, userCountryDateFormat),
      currencyTypeId,
      initialAmount,
      ...(expiryDate && {
        expiryDate: convertToUTCFormat(expiryDate, userCountryDateFormat, true),
      }),
      notes,
      statusId,
    };

    return params;
  };

  const callApiAndDismissModals = () => {
    if (buttonClickedRef.current === buttonSave) {
      setShowConfirmatoryPopup(false);
      clearRefs();

      if (getValuesFromForm()) {
        addPurchaseOrder(getValuesFromForm());
      }
    } else if (buttonClickedRef.current === buttonUpdate) {
      setShowConfirmatoryPopup(false);
      clearRefs();
      if (purchaseOrderData) {
        const updatedParams: UpdatePurchaseOrderParams = {
          purchaseOrderId: purchaseOrderData.purchaseOrderId,
          ...getValuesFromForm(),
        };

        if (updatedParams) {
          updatePurchaseOrder(updatedParams);
        }
      }
    } else {
      setShowConfirmatoryPopup(false);
      onClose();
      clearRefs();
    }
  };

  const handleCurrencyChange = () => {
    const { currencyTypeId } = getValues();

    const currencyName = getNameByIdFromList(currencyList, currencyTypeId);
    const currencyFormat = CURRENCY_FORMAT_MAP[currencyName];

    setCurrencyLocale(currencyFormat?.locale);
  };

  const handleCurrencyFormattedValues = (
    value: string,
    valueNonFormatted: string,
  ) => {
    setValue(AddPurchaseOrderFormKey.initialAmount, valueNonFormatted);
    setValue(AddPurchaseOrderFormKey.initialAmountFormatted, value);
  };

  const getDateValue = (key: keyof typeof AddPurchaseOrderFormKey) => {
    const value = isNotEmpty(getValues(key))
      ? (getValues(key) as unknown as string)
      : null;
    return dayjs(value, userCountryDateFormat);
  };

  const specialCharsAllowedValidation = {
    onInput: (e: React.FormEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      target.value = target.value.replace(
        REGEX_ACCEPTED_PO_NUMBER_SPECIAL_CHARS_AND_SPACES,
        "",
      );
    },
  };

  const modalTitle = useMemo(() => {
    return isEdit && purchaseOrderData
      ? `${replaceHashWithValue(getLabel(editPurchaseOrderHeading), purchaseOrderData?.poNumber)}`
      : getLabel(addPurchaseOrderHeading);
  }, [isEdit]);

  const modalPrimaryButtonTitle = isEdit ? buttonUpdate : buttonSave;
  const minDate = dayjs(new Date().getTime() + ONE_DAY_IN_MS);

  const handleEffectiveDateValidation = (newValue: dayjs.Dayjs | null) => {
    setValue(AddPurchaseOrderFormKey.effectiveDateWithoutFormatted, newValue);
  };
  const handleExpireDateValidation = (newValue: dayjs.Dayjs | null) => {
    setValue(AddPurchaseOrderFormKey.expiryDateWithoutFormatted, newValue);
  };

  return (
    <MuiModal
      open={open}
      onClose={handleOnClose}
      title={modalTitle}
      hasLargeContent
      primaryButtonTitle={modalPrimaryButtonTitle}
      secondaryButtonTitle={buttonCancel}
      onSecondaryButtonClick={() => handleConfirmatoryPopup(buttonCancel)}
      onPrimaryButtonClick={() => handleSubmit(checkFieldValidations)()}
    >
      <FormProvider {...methods}>
        <AddPurchaseOrderWrapper>
          <MuiBox className="content">
            <MuiGrid container columnSpacing={2} rowSpacing={isMobile ? 2 : 5}>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={AddPurchaseOrderFormKey.poNumber}
                >
                  <MuiTextField
                    isMandatory
                    id={`${pageId}-${AddPurchaseOrderFormKey.poNumber}`}
                    label={getLabel(poNumber)}
                    placeholder={commonLabels.inputPlaceHolder}
                    maxLength={MAX_LENGTH_30}
                    inputProps={specialCharsAllowedValidation}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiDateFieldFormController
                  control={control}
                  name={AddPurchaseOrderFormKey.effectiveDate}
                  id={`${pageId}-${AddPurchaseOrderFormKey.effectiveDate}`}
                  value={getDateValue(AddPurchaseOrderFormKey.effectiveDate)}
                  label={getLabel(poEffectiveDate)}
                  valueFormat={userCountryDateFormat}
                  clearable
                  isMandatory
                  className="date-picker"
                  format={userCountryDateFormat}
                  onChange={handleEffectiveDateValidation}
                  requiredARIALabel={commonARIALabel.required}
                  selectDateARIALabel={commonARIALabel.selectDate}
                />
              </MuiGrid>
              <MuiGrid size={gridSize} className="horizontal-direction">
                <FormController
                  control={control}
                  name={AddPurchaseOrderFormKey.currencyTypeId}
                >
                  <MuiSelect
                    id={`${pageId}-${AddPurchaseOrderFormKey.currencyTypeId}`}
                    className="currency-container"
                    isMandatory
                    label={getLabel(poAmount)}
                    visibleDefaultSelectOption={false}
                    options={setApiDataToDropDownOption(currencyList)}
                    onChange={(e) => {
                      handleCurrencyChange();
                    }}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
                <MuiBox className="currency-container-textfield">
                  <FormController
                    control={control}
                    name={AddPurchaseOrderFormKey.initialAmountFormatted}
                  >
                    <TextFieldCurrency
                      id={`${pageId}-${AddPurchaseOrderFormKey.initialAmountFormatted}`}
                      formId={AddPurchaseOrderFormKey.initialAmountFormatted}
                      locale={currencyLocale}
                      isCurrencyChanged={`${getValues(
                        AddPurchaseOrderFormKey.currencyTypeId,
                      )}${currencyLocale}`}
                      isMandatory
                      isLabelInvisible
                      label={getLabel(poAmount)}
                      placeholder={commonLabels.inputPlaceHolder}
                      zeroValidationMessage={fetchValidationMessage(
                        commonSiteCoreKeys.greaterThanZeroFieldValidator,
                      )}
                      formatErrorMessage={fetchValidationMessage(
                        commonSiteCoreKeys.alphabetNotAllowedValidator,
                      )}
                      getFinalValues={handleCurrencyFormattedValues}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiBox>
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiDateFieldFormController
                  control={control}
                  name={AddPurchaseOrderFormKey.expiryDate}
                  value={getDateValue(AddPurchaseOrderFormKey.expiryDate)}
                  disablePast
                  id={`${pageId}-${AddPurchaseOrderFormKey.expiryDate}`}
                  minDate={minDate}
                  label={getLabel(poExpirationDate)}
                  className="date-picker"
                  clearable
                  format={userCountryDateFormat}
                  valueFormat={userCountryDateFormat}
                  onChange={handleExpireDateValidation}
                  requiredARIALabel={commonARIALabel.required}
                  selectDateARIALabel={commonARIALabel.selectDate}
                />
              </MuiGrid>

              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={AddPurchaseOrderFormKey.statusId}
                >
                  <MuiSelect
                    id={`${pageId}-${AddPurchaseOrderFormKey.statusId}`}
                    disabled={!isEdit}
                    label={getLabel(poStatus)}
                    options={fetchDropdownValues(
                      purchaseOrderTitle,
                      statusList,
                    )}
                    placeholder={commonLabels.selectPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>
            <MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiBox className="notes-container">
                  <FormController
                    control={control}
                    name={AddPurchaseOrderFormKey.notes}
                  >
                    <MuiTextField
                      id={`${pageId}-${AddPurchaseOrderFormKey.notes}`}
                      label={commonLabels.notes}
                      placeholder={commonLabels.inputPlaceHolder}
                      className="notes-input"
                      maxLength={MAX_LENGTH_1000}
                      multiline
                      rows={4}
                      data-testid={`${pageId}-${AddPurchaseOrderFormKey.notes}`}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiBox>
              </MuiGrid>
            </MuiGrid>
          </MuiBox>
          {showConfirmatoryPopup && (
            <MuiModal
              id={COMPONENT_IDS.CONFIRMATION_MODAL}
              onClose={hideConfirmatoryPopup}
              title={commonLabels.confirmation}
              open={showConfirmatoryPopup}
              onPrimaryButtonClick={callApiAndDismissModals}
              onSecondaryButtonClick={hideConfirmatoryPopup}
              secondaryButtonTitle={commonLabels.no}
              primaryButtonTitle={commonLabels.yes}
            >
              <ConfirmatoryPopupWrapper>
                <MuiTypography
                  id={`${pageId}-confirmation-message`}
                  variant="h3"
                  className="confirmatory-message"
                >
                  {confirmatoryMessageRef.current}
                </MuiTypography>
              </ConfirmatoryPopupWrapper>
            </MuiModal>
          )}
        </AddPurchaseOrderWrapper>
      </FormProvider>
    </MuiModal>
  );
};
